import {
  AdminCreateThreadResponse,
  Route,
  SetSupportChatMessageReadStatus,
  SupportChatMessageResponse,
  SupportChatThreadResponse,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

async function fetchAdminThreads(): Promise<SupportChatThreadResponse[]> {
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__ADMIN_FIND_THREADS),
    method: "GET",
  })
}

async function fetchAdminMessages(
  threadId: string,
): Promise<SupportChatMessageResponse[]> {
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__ADMIN_FIND_MESSAGES, threadId),
    method: "GET",
  })
}

async function sendMessageAsAdmin(
  threadId: string,
  data: { content: string; attachedFiles?: File[] },
): Promise<void> {
  const formData = new FormData()
  formData.append("content", data.content)
  if (data.attachedFiles) {
    for (const file of data.attachedFiles) {
      formData.append("attachedFiles", file)
    }
  }
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__ADMIN_SEND_MESSAGE, threadId),
    method: "POST",
    data: formData,
  })
}

async function markAsReadForAdmin(threadId: string): Promise<void> {
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__ADMIN_MARK_READ, threadId),
    method: "PATCH",
  })
}

async function setMessageReadStatusAsAdmin(
  threadId: string,
  data: SetSupportChatMessageReadStatus,
): Promise<void> {
  return request({
    url: populateRoute(
      Route.SUPPORT_CHAT__ADMIN_SET_MESSAGE_READ_STATUS,
      threadId,
    ),
    method: "PATCH",
    data,
  })
}

async function createAdminThread(
  storeId: string,
): Promise<AdminCreateThreadResponse> {
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__ADMIN_CREATE_THREAD, storeId),
    method: "POST",
  })
}

async function fetchAdminUnreadCount(): Promise<number> {
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__ADMIN_UNREAD_COUNT),
    method: "GET",
  })
}

const supportChatQueries = {
  all: () => ["support-chat"],
  adminThreads: () =>
    queryOptions({
      queryKey: [...supportChatQueries.all(), "admin-threads"],
      queryFn: () => fetchAdminThreads(),
    }),
  adminMessages: (threadId: string) =>
    queryOptions({
      queryKey: [...supportChatQueries.all(), "admin-messages", threadId],
      queryFn: () => fetchAdminMessages(threadId),
    }),
  adminUnreadCount: () =>
    queryOptions({
      queryKey: [...supportChatQueries.all(), "admin-unread-count"],
      queryFn: () => fetchAdminUnreadCount(),
      staleTime: 5000,
    }),
}

export const useSendAdminSupportMessage = (threadId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { content: string; attachedFiles?: File[] }) =>
      sendMessageAsAdmin(threadId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(supportChatQueries.adminMessages(threadId))
      queryClient.invalidateQueries(supportChatQueries.adminThreads())
    },
  })
}

export const useMarkAdminSupportChatAsRead = (threadId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => markAsReadForAdmin(threadId),
    onSuccess: () => {
      queryClient.invalidateQueries(supportChatQueries.adminMessages(threadId))
      queryClient.invalidateQueries(supportChatQueries.adminUnreadCount())
      queryClient.invalidateQueries(supportChatQueries.adminThreads())
    },
  })
}

export const useSetAdminSupportChatMessageReadStatus = (threadId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SetSupportChatMessageReadStatus) =>
      setMessageReadStatusAsAdmin(threadId, data),
    onSettled: () => {
      queryClient.invalidateQueries(supportChatQueries.adminMessages(threadId))
      queryClient.invalidateQueries(supportChatQueries.adminThreads())
      queryClient.invalidateQueries(supportChatQueries.adminUnreadCount())
    },
  })
}

export const useCreateAdminThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (storeId: string) => createAdminThread(storeId),
    onSuccess: () => {
      queryClient.invalidateQueries(supportChatQueries.adminThreads())
    },
  })
}

export default supportChatQueries
